import { useCallback } from 'react';
import useGetAttachmentUploadUrl from '#/common/components/AttachmentUpload/hooks/useAttachmentUploadUrlMutation';
import useAttachmentCreate from '#/common/components/AttachmentUpload/hooks/useAttachmentCreateMutation';
import useToast from './useToast';

// Hook to upload scanned file attached to the created record
const useUploadScannedFile = () => {
  const { alert } = useToast();
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();

  const uploadScannedFile = useCallback(
    async (table: string, id: string, file: File) => {
      try {
        // Get upload URL
        const uploadInfos = await getAttachmentUploadUrls([
          {
            fileName: file.name,
            size: file.size,
            contentType: file.type,
            relatedTable: table,
            relatedItem: id,
            type: 'general',
          },
        ]);

        const uploadInfo = Array.isArray(uploadInfos)
          ? uploadInfos.find((u: any) => u?.uploadUrl && u.uploadName)
          : undefined;

        if (!uploadInfo?.uploadUrl || !uploadInfo?.uploadName) {
          throw new Error('Failed to get upload URL for scanned file');
        }

        // Upload file to S3
        const uploadResponse = await fetch(uploadInfo.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
        }

        // Create attachment record
        await createAttachment({
          fileName: String(uploadInfo.uploadName).split('/').pop() || '',
          size: file.size,
          mimeType: file.type,
          relatedTable: table,
          relatedItem: id,
          type: 'general',
          path: uploadInfo.uploadName,
          originalFileName: file.name,
        });
      } catch (error) {
        console.error('Failed to upload scanned file:', error);
        const errorMsg = ['Failed to upload scanned image attachment!'];
        if (typeof error === 'string') {
          errorMsg.push(error);
        }
        if (error instanceof Error) {
          errorMsg.push(error.message);
        }
        void alert('error', errorMsg.join(' '));
        throw error;
      }
    },
    [alert, getAttachmentUploadUrls, createAttachment],
  );

  return uploadScannedFile;
};

export default useUploadScannedFile;
